'use client';

import { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { supportTicketService } from '../../lib/services/supportTicketService';
import { COMPANY } from '../../lib/legal';
import PageHead from '../customer-homepage/components/PageHead';

export default function CustomerSupport() {
  const { user, loading: authLoading } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
    priority: 'medium'
  });

  // FAQ state
  const [expandedFaq, setExpandedFaq] = useState(null);

  // Load user's tickets
  useEffect(() => {
    if (user) {
      loadUserTickets();
    } else if (!authLoading) {
      setLoading(false);
    }
  }, [user, authLoading]);

  const loadUserTickets = async () => {
    try {
      setLoading(true);
      const userTickets = await supportTicketService?.getUserTickets();
      setTickets(userTickets);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitTicket = async (e) => {
    e?.preventDefault();

    if (!formData?.subject?.trim() || !formData?.message?.trim()) {
      setError('Please fill in both subject and message');
      return;
    }

    try {
      setCreating(true);
      setError('');

      const newTicket = await supportTicketService?.createTicket(formData);

      // Add new ticket to list
      setTickets((prevTickets) => [newTicket, ...prevTickets]);

      // Reset form
      setFormData({
        subject: '',
        message: '',
        priority: 'medium'
      });
      setShowForm(false);

    } catch (err) {
      setError(err?.message);
    } finally {
      setCreating(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open':return 'bg-[rgba(255,84,112,0.14)] text-error';
      case 'in_progress':return 'bg-[rgba(255,201,77,0.14)] text-warning';
      case 'resolved':return 'bg-[rgba(50,216,0,0.14)] text-accent-bright';
      case 'closed':return 'bg-white/[0.08] text-muted-foreground';
      default:return 'bg-white/[0.08] text-muted-foreground';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent':return 'bg-error text-error-foreground';
      case 'high':return 'bg-warning text-warning-foreground';
      case 'medium':return 'bg-white/[0.14] text-foreground';
      case 'low':return 'bg-white/[0.06] text-muted-foreground';
      default:return 'bg-white/[0.06] text-muted-foreground';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  // FAQ data
  const faqData = [
  {
    category: "Your account",
    question: "Do I need to verify my account?",
    answer: "No. You just need an account, and you must be 18 or over to enter. If you win, we check your identity and address before the prize is handed over, as our terms explain."
  },
  {
    category: "Payments",
    question: "Why is my payment failing?",
    answer: "Check your card details and that your billing address matches your bank's records. If it still fails, contact your bank or send us a message."
  },
  {
    category: "Draws and entries",
    question: "When are competition draws held?",
    answer: "Every competition closing on a night is drawn together in one live session on Facebook, shortly after entries close. The draw time is on each competition page, and every winner is listed on our results page."
  },
  {
    category: "Draws and entries",
    question: "Can I get a refund if I change my mind?",
    answer: "No. Entries are final and can't be refunded, as set out in our terms and conditions. If we ever have to cancel a competition, everyone who entered is refunded."
  },
  {
    category: "Draws and entries",
    question: "Can I enter for free?",
    answer: "Yes. Every competition can be entered free by post, and postal entries go into the same draw. Our Free postal entry page explains how."
  },
  {
    category: "Your account",
    question: "I forgot my password, what should I do?",
    answer: "Use the 'Forgot your password?' link on the log-in page to reset your password. Check your email for reset instructions."
  },
  {
    category: "Payments",
    question: "How long does it take for payments to process?",
    answer: "Card and wallet payments go through straight away, and your ticket numbers show in your account as soon as the payment is confirmed."
  }];


  const pageHead = (
    <PageHead
      eyebrow="Help"
      title="Help centre."
      lede="Need help? Send us a message, or find answers to common questions below."
    />
  );

  const inputCls =
    'w-full px-4 py-3 rounded-[6px] border border-white/[0.12] bg-black/30 text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-colors';

  if (loading) {
    return (
      <>
        {pageHead}
        <section className="bg-background">
          <div className="max-w-[1520px] mx-auto px-[var(--gutter)] py-[clamp(2.5rem,6vw,5rem)]">
            <div className="flex items-center gap-3">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent"></div>
              <p className="text-muted-foreground">Loading the help centre…</p>
            </div>
          </div>
        </section>
      </>);

  }

  return (
    <>
      {pageHead}
      <section className="bg-background">
        <div className="max-w-[1520px] mx-auto px-[var(--gutter)] py-[clamp(2.5rem,6vw,5rem)]">

          {/* Error Display */}
          {error &&
          <div className="max-w-[46rem] mb-8 rounded-[6px] border border-[rgba(255,84,112,0.35)] bg-[rgba(255,84,112,0.10)] p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-error" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-error">{error}</p>
                </div>
                <div className="ml-auto pl-3">
                  <button
                  onClick={() => setError('')}
                  className="text-error opacity-60 hover:opacity-100">

                    <span className="sr-only">Dismiss</span>
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          }

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-10 items-start">

            {/* Ticket Creation Section */}
            <div className="rounded-[8px] border border-white/10 bg-[#111612] p-5 sm:p-6">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <h2 className="d text-[length:var(--t-h3)]">Contact us.</h2>
                {!showForm && user &&
                <button
                  onClick={() => setShowForm(true)}
                  className="btn-block btn-go !text-[0.9rem] !px-4">

                    New ticket
                  </button>
                }
              </div>

              {showForm ?
              <form onSubmit={handleSubmitTicket} className="space-y-5">
                  <div>
                    <label htmlFor="subject" className="block text-sm font-semibold text-foreground mb-2">
                      Subject *
                    </label>
                    <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData?.subject}
                    onChange={handleInputChange}
                    className={inputCls}
                    placeholder="A short summary of your question"
                    required />

                  </div>

                  <div>
                    <label htmlFor="priority" className="block text-sm font-semibold text-foreground mb-2">
                      Priority
                    </label>
                    <select
                    id="priority"
                    name="priority"
                    value={formData?.priority}
                    onChange={handleInputChange}
                    className={`${inputCls} [color-scheme:dark]`}>

                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-foreground mb-2">
                      Message *
                    </label>
                    <textarea
                    id="message"
                    name="message"
                    value={formData?.message}
                    onChange={handleInputChange}
                    rows={6}
                    className={`${inputCls} resize-none`}
                    placeholder="Tell us what happened, with any order or ticket numbers."
                    required />

                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                    type="submit"
                    disabled={creating}
                    className="btn-block btn-go flex-1 disabled:opacity-50 disabled:cursor-not-allowed">

                      {creating ?
                    <span className="flex items-center justify-center">
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-accent-foreground" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Sending…
                        </span> :

                    'Send ticket'
                    }
                    </button>
                    <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setFormData({ subject: '', message: '', priority: 'medium' });
                      setError('');
                    }}
                    className="btn-block btn-line">

                      Cancel
                    </button>
                  </div>
                </form> :

              <div className="rounded-[6px] border border-dashed border-white/[0.14] py-10 px-4 text-center text-muted-foreground">
                  <svg className="mx-auto h-10 w-10 text-muted-foreground mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-3.582 8-8 8a8.13 8.13 0 01-2.939-.515L7 21l.544-2.937A8.002 8.002 0 0121 12z" />
                  </svg>
                  {user ?
                  <p>Select “New ticket” to send us a message.</p> :

                  <p>
                      <a href="/customer-login?next=%2Fcustomer-support" className="font-semibold text-accent-bright underline underline-offset-2">
                        Log in
                      </a>{' '}
                      to send us a message, or email{' '}
                      <a href={`mailto:${COMPANY.email}`} className="font-semibold text-accent-bright underline underline-offset-2">
                        {COMPANY.email}
                      </a>
                      .
                    </p>
                  }
                </div>
              }

              {/* Your Tickets Section */}
              {user &&
              <div className="mt-10 pt-6 border-t border-white/[0.12]">
                <h3 className="font-semibold text-lg text-foreground mb-4">Your tickets</h3>

                {tickets?.length > 0 ?
                <div className="space-y-3">
                    {tickets?.map((ticket) =>
                  <div key={ticket?.id} className="rounded-[6px] border border-white/10 bg-black/20 p-4">
                        <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 mb-2">
                          <h4 className="font-semibold text-foreground flex-1 min-w-0 break-words">{ticket?.subject}</h4>
                          <div className="flex gap-2">
                            <span className={`px-2 py-1 rounded-[4px] text-xs font-semibold capitalize ${getPriorityColor(ticket?.priority)}`}>
                              {ticket?.priority}
                            </span>
                            <span className={`px-2 py-1 rounded-[4px] text-xs font-semibold capitalize ${getStatusColor(ticket?.status)}`}>
                              {ticket?.status?.replace('_', ' ')}
                            </span>
                          </div>
                        </div>
                        <p className="text-muted-foreground text-sm mb-3 line-clamp-2">{ticket?.message}</p>
                        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-xs text-muted-foreground">
                          <span>Created: {formatDate(ticket?.createdAt)}</span>
                          {ticket?.resolvedAt &&
                      <span>Resolved: {formatDate(ticket?.resolvedAt)}</span>
                      }
                        </div>
                      </div>
                  )}
                  </div> :

                <div className="py-6 text-muted-foreground flex items-center gap-3">
                    <svg className="h-6 w-6 shrink-0 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p>No tickets sent yet.</p>
                  </div>
                }
              </div>
              }
            </div>

            {/* FAQ Section */}
            <div className="rounded-[8px] border border-white/10 bg-[#111612] p-5 sm:p-6">
              <h2 className="d text-[length:var(--t-h3)] mb-4">Common questions.</h2>

              <div className="border-b border-white/[0.12]">
                {faqData?.map((faq, index) =>
                <div key={index} className="border-t border-white/[0.12]">
                    <button
                    onClick={() => toggleFaq(index)}
                    aria-expanded={expandedFaq === index}
                    className="w-full py-4 text-left flex items-center justify-between gap-4 group">

                      <div>
                        <div className="text-xs text-accent-bright font-semibold mb-1">{faq?.category}</div>
                        <div className="font-semibold text-foreground group-hover:text-accent-bright transition-colors">{faq?.question}</div>
                      </div>
                      <svg
                      className={`h-5 w-5 shrink-0 text-muted-foreground transition-transform ${
                      expandedFaq === index ? 'transform rotate-180' : ''}`
                      }
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24">

                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {expandedFaq === index &&
                  <div className="pb-5 pr-9 leading-[1.6] text-foreground/85">
                        {faq?.answer}
                      </div>
                  }
                  </div>
                )}
              </div>

              {/* Contact Information */}
              <div className="mt-10">
                <h3 className="font-semibold text-lg text-foreground mb-3">Other ways to reach us</h3>

                <div className="flex items-center">
                  <svg className="h-5 w-5 shrink-0 text-accent-bright mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span className="text-foreground min-w-0 break-words">
                    Email:{' '}
                    <a href={`mailto:${COMPANY.email}`} className="font-semibold text-accent-bright underline underline-offset-2">
                      {COMPANY.email}
                    </a>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>);

}
